// This is a component that renders a list of given datasets it changes when user picks a different point it also sets the selected dataset.
import React, { useEffect, useState } from 'react';
import { Input, Select } from 'antd';
import styled from 'styled-components';
import { useMDSContext } from '@metservice/use-mds';

import { datasourceDataset } from '@mono/types/src/mds';
import { sanitize } from '@mono/fuzzy-search';
import { useSpaceConfiguratorContext } from 'space-configurator-context';

interface SelectProps {
    datasetList: datasourceDataset[] | undefined;
}

export const GriddedDatasetSelector = ({ datasetList }: SelectProps) => {
    const [selectableCapabilities, setSelectableCapabilities] = useState<string[]>([]);
    const [loadingCapabilities, setLoadingCapabilities] = useState<boolean>(false);

    const { findCapabilitiesByDataset, capabilities } = useMDSContext();
    const { state, setState } = useSpaceConfiguratorContext();

    // When the selected dataset changes, we need to update the selectable capabilities
    useEffect(() => {
        if (!state.griddedDatasetId) {
            setSelectableCapabilities([]);
            return;
        }

        const abortController = new AbortController();
        setLoadingCapabilities(true);

        const loadCapabilitiesForDataset = async () => {
            if (!state.griddedDatasetId) return;
            const { capabilities } = await findCapabilitiesByDataset(state.griddedDatasetId, abortController.signal);
            setLoadingCapabilities(false);
            setSelectableCapabilities(capabilities.map((c) => c.id));
        };

        loadCapabilitiesForDataset();

        return () => {
            setLoadingCapabilities(false);
            abortController.abort();
        };
    }, [state.griddedDatasetId]);

    const isCapabilitySelectorDisabled = !state.griddedDatasetId || loadingCapabilities;

    return (
        <div style={{ display: 'inline-flex', alignItems: 'center', width: '100%', flexDirection: 'column', gap: 2 }}>
            <div style={{ display: 'flex', flexDirection: 'row', width: '100%' }}>
                <Input disabled value={'Data'} style={{ width: 71, color: 'rgba(0, 0, 0, 0.85)', cursor: 'auto', marginRight: -1, textAlign: 'center' }} />
                <StyledSelect
                    allowClear
                    style={{ flexGrow: 1 }}
                    value={state.griddedDatasetId}
                    onClear={() => {
                        setState((prev) => ({
                            ...prev,
                            griddedDatasetId: undefined,
                            griddedCapabilityId: undefined,
                            obsId: undefined,
                            obsCapabilityId: undefined,
                        }));
                    }}
                    onChange={(id: string) => {
                        setState((prev) => ({ ...prev, griddedDatasetId: id, griddedCapabilityId: undefined, obsId: undefined, obsCapabilityId: undefined }));
                    }}
                    options={datasetList?.map((dataset) => {
                        return { label: dataset.display_name_short, value: dataset.id };
                    })}
                    loading={!datasetList}
                    disabled={!datasetList}
                    showSearch
                    filterOption={(input, option) => sanitize(option?.label).includes(sanitize(input))}
                    placeholder='Select a dataset'
                />
            </div>
            <div style={{ display: 'flex', flexDirection: 'row', width: '100%' }}>
                <Input disabled value={'Type'} style={{ width: 71, color: 'rgba(0, 0, 0, 0.85)', cursor: 'auto', marginRight: -1, textAlign: 'center' }} />
                <StyledSelect
                    allowClear
                    disabled={isCapabilitySelectorDisabled}
                    style={{ flexGrow: 1 }}
                    value={state.griddedCapabilityId}
                    onClear={() => {
                        setState((prev) => ({ ...prev, griddedCapabilityId: undefined, obsId: undefined, obsCapabilityId: undefined }));
                    }}
                    onChange={(capabilityId: string) => {
                        setState((prev) => ({ ...prev, griddedCapabilityId: capabilityId, obsId: undefined, obsCapabilityId: undefined }));
                    }}
                    options={selectableCapabilities.map((capabilityId) => {
                        const capability = capabilities.find((c) => c.id === capabilityId);
                        if (!capability) return { label: 'unknown', value: capabilityId };
                        return { label: capability.display_name_short, value: capability.id };
                    })}
                    loading={loadingCapabilities}
                    showSearch
                    filterOption={(input, option) => sanitize(option?.label).includes(sanitize(input))}
                    placeholder={isCapabilitySelectorDisabled ? '' : 'Select a weather type'}
                />
            </div>
        </div>
    );
};
const StyledSelect = styled(Select)`
    height: 32px;
    span {
        color: black !important;
    }
`;
